import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import {
  FOOTBALL_PLAY_PRO,
  FOOTBALL_PLAY_PRO_ONBOARDING,
} from '@sorare/core/src/constants/__generated__/routes';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';
import { toRelative } from '@sorare/core/src/lib/urls';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FixtureTeamsOverview } from '@sorare/gameplay/src/components/fixture/FixtureTeamsOverview';

import { Banner } from './Banner';
import { ProBlockLinkBox } from './ProBlockLinkBox';
import {
  ProBlockQuery,
  ProBlockQueryVariables,
  ProBlock_config,
  ProBlock_so5Root,
} from './__generated__/index.graphql';

const PRO_BLOCK_QUERY = gql`
  query ProBlockQuery {
    so5 {
      id
      ongoingSo5Fixture: so5Fixture(type: MY_ONGOING, sport: FOOTBALL) {
        slug
        ...FixtureTeamsOverview_so5Fixture
      }
    }
  }
  ${FixtureTeamsOverview.fragments.so5Fixture}
` as TypedDocumentNode<ProBlockQuery, ProBlockQueryVariables>;

const BannerWrapper = styled(LinkOther).attrs({ as: Link })`
  width: 100%;
  @media ${laptopAndAbove} {
    padding: var(--unit) var(--unit) 0;
  }
`;
const BottomWrapper = styled(Vertical).attrs({ center: true, gap: 3 })`
  align-self: stretch;

  @media ${laptopAndAbove} {
    align-self: center;
  }
`;
const Bottom = styled(Horizontal).attrs({ gap: 1.5 })`
  align-self: stretch;
`;
const LinkOverlayWrapper = styled(LinkOverlay)`
  flex: 1;

  && > * {
    @media ${laptopAndAbove} {
      min-width: 300px;
    }
  }
`;

type Props = { config: ProBlock_config; so5Root: ProBlock_so5Root };
export const ProBlock = ({ config, so5Root }: Props) => {
  const { upcomingSo5Fixture } = so5Root;
  const { data } = useQuery(PRO_BLOCK_QUERY);
  const { ongoingSo5Fixture } = data?.so5 || {};
  const {
    flags: { useSelectFavoriteClubOnboarding = false },
  } = useFeatureFlags();

  const { lobbyTile } = config;
  const bannerTo = lobbyTile?.ctaHref
    ? toRelative(lobbyTile.ctaHref)
    : FOOTBALL_PLAY_PRO;
  const navigate = useNavigate();

  return (
    <ProBlockLinkBox withBanner={!useSelectFavoriteClubOnboarding}>
      <OnboardingHighlightWrapper
        highlightName="PlayBanners"
        config={{
          onClick: () => {
            navigate(FOOTBALL_PLAY_PRO_ONBOARDING, { replace: true });
          },
          filter: 'drop-shadow(0px 5px 10px #D20CFD)',
        }}
      >
        <BannerWrapper to={bannerTo}>
          {!useSelectFavoriteClubOnboarding && <Banner config={config} />}
        </BannerWrapper>
      </OnboardingHighlightWrapper>
      <BottomWrapper>
        {upcomingSo5Fixture && (
          <LabelM bold color="var(--c-yellow-400)">
            <TimeLeft time={upcomingSo5Fixture.startDate}>
              {({ timeLeft, isEnded }) =>
                isEnded ? (
                  <FormattedMessage
                    id="Play.weeklyEnded"
                    defaultMessage="Next Game Week started"
                  />
                ) : (
                  <FormattedMessage
                    id="Play.weeklyCountdown"
                    defaultMessage="Next Game Week starts in {timeLeft}"
                    values={{ timeLeft }}
                  />
                )
              }
            </TimeLeft>
          </LabelM>
        )}
        <Bottom>
          {ongoingSo5Fixture && (
            <LinkOther as="div">
              <FixtureTeamsOverview
                so5Fixture={ongoingSo5Fixture}
                trapClickOutside
                transparent={false}
              />
            </LinkOther>
          )}
          <LinkOverlayWrapper as={Link} to={FOOTBALL_PLAY_PRO}>
            <Button asSpan size="medium" color="primary">
              <FormattedMessage {...glossary.playNow} />
            </Button>
          </LinkOverlayWrapper>
        </Bottom>
      </BottomWrapper>
    </ProBlockLinkBox>
  );
};

ProBlock.fragments = {
  config: gql`
    fragment ProBlock_config on Config {
      id
      lobbyTile(sport: FOOTBALL, position: BANNER) {
        id
        ctaHref
      }
      ...Banner_config
    }
    ${Banner.fragments.config}
  ` as TypedDocumentNode<ProBlock_config>,
  so5Root: gql`
    fragment ProBlock_so5Root on So5Root {
      upcomingSo5Fixture: so5Fixture(type: UPCOMING) {
        slug
        startDate
      }
    }
  ` as TypedDocumentNode<ProBlock_so5Root>,
};
